import React from 'react';
import { Box, TextField, IconButton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useViewport } from '../../../shared/hooks/useViewport';
import { TOUCH_TARGET_SX } from '../../../shared/constants/layout';

interface MessageEditModeProps {
  editContent: string;
  isUser: boolean;
  onContentChange: (content: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

const MessageEditMode: React.FC<MessageEditModeProps> = ({
  editContent,
  isUser,
  onContentChange,
  onSave,
  onCancel,
}) => {
  const theme = useTheme();
  const { isTouch } = useViewport();
  const touchTargetSx = isTouch ? TOUCH_TARGET_SX : {};

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={15}
        value={editContent}
        onChange={(e) => onContentChange(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            color: isUser ? 'primary.contrastText' : 'text.primary',
            '& fieldset': {
              borderColor: isUser
                ? alpha(theme.palette.common.white, 0.3)
                : alpha(theme.palette.common.black, 0.23),
            },
          },
        }}
      />
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <IconButton
          size="small"
          onClick={onSave}
          aria-label="Save edit"
          sx={{ ...touchTargetSx, color: isUser ? 'primary.contrastText' : 'primary.main' }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onCancel}
          aria-label="Cancel edit"
          sx={{ ...touchTargetSx, color: isUser ? 'primary.contrastText' : 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default MessageEditMode;
